import { produce } from "immer";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useTts } from "tts-react";

import {
  Button,
  colorFromTheme,
  HSpace,
  inputGlow,
  RichContentDisplay,
  VSpace,
} from "@examspark/common-ui";
import {
  IconPauseLine,
  IconPlayLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  ExamConfigContext,
  ExamFlagsContext,
  ExamToolsContext,
} from "@examspark/exam-ui-context";
import { Stem, StemChoice } from "@examspark/types";
import { Mark } from "@examspark/types/dist/item-stem";

import MarkComponent from "../common/Mark";
import { shuffleQuestionOptions } from "../common/shuffle";
import QuestionContext from "../QuestionContext";
import letters from "../utils/letters";
import { extractSpeechText } from "../utils/speechTextExtraction";

import Prompt from "./shared/Prompt";

export type ChoiceProps = {
  data: StemChoice;
};

const MultipleChoice: React.FC<React.PropsWithChildren<ChoiceProps>> = (
  props,
) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);
  const examTools = useContext(ExamToolsContext);

  const response = player.response?.[props.data.id] as
    | Partial<Stem.Response>
    | undefined;

  // Reveal Key
  useEffect(() => {
    if (player.revealKey) {
      const keys: any[] = [];
      for (const i in props.data.scoring?.options) {
        if (props.data.scoring.options[i].isKey) {
          keys.push(i);
        }
      }

      dispatchPlayer({
        type: "RESPONSE",
        data: {
          id: props.data.id,
          score: true,
          response: {
            options: keys,
          } satisfies Partial<Stem.Response>,
        },
      });
    }
  }, [
    player.revealKey,
    dispatchPlayer,
    props.data.id,
    props.data.scoring?.options,
  ]);

  // null means no limit
  const maxChoices =
    props.data.config?.allowMultipleKeys && props?.data?.config?.maxChoices
      ? +props?.data?.config?.maxChoices
      : null;

  const currentlySelectedChoiceCount = response?.options?.length || 0;

  const canSelectMoreChoices =
    maxChoices === null || currentlySelectedChoiceCount < maxChoices;

  const handleOptionClick = (option: Stem.Option) => {
    const rsp = produce(response || {}, (draft) => {
      if (!draft.options) {
        draft.options = [];
      }

      if (examTools?.isResponseMasking) {
        if (!draft.maskedOptions) {
          draft.maskedOptions = [];
        }
        // only mask if the option is not selected
        if (!draft.options.includes(option.id)) {
          if (draft.maskedOptions.includes(option.id)) {
            // unmask
            draft.maskedOptions = draft.maskedOptions.filter(
              (id: string) => id !== option.id,
            );
          } else {
            // mask
            draft.maskedOptions.push(option.id);
          }
        }
      } else {
        // only select if the option is not masked
        if (!draft.maskedOptions?.includes(option.id)) {
          if (props?.data?.config?.allowMultipleKeys) {
            const idx = draft.options.indexOf(option.id);
            if (idx !== -1) {
              // removing
              draft.options.splice(idx, 1);
            } else if (canSelectMoreChoices) {
              // adding
              draft.options.push(option.id);
            }
          } else {
            draft.options = [option.id];
          }
        }
      }
    });

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: rsp,
      },
    });
  };

  // if shuffle is enabled on the item, and the context allows it, shuffle the
  // options
  const shuffleSeed = props.data.config?.enableShuffle
    ? (player.shuffleSeed ?? null)
    : null;
  const maybeShuffledOptions = shuffleQuestionOptions(
    shuffleSeed,
    props.data.content.options ?? [],
  );

  return (
    <div className="item-player-element item-player-stem">
      <VSpace size="2rem">
        <Prompt data={props.data.content.prompt} />
        <VSpace size="0.5rem">
          {maybeShuffledOptions.map((choice, idx) => {
            const choiceClasses: any[] = [];
            choiceClasses.push("item-player-mc-option");

            const isSelected = response?.options?.includes(choice.id);

            // Score responses
            let mark: Mark;
            if (player.scoreResponses) {
              if (isSelected) {
                mark = "incorrect";
                if (props?.data?.scoring?.options?.[choice.id]?.isKey) {
                  mark = "correct";
                }
              }
            }

            return (
              <MultipleChoiceOption
                key={choice.id}
                choice={choice}
                idx={idx}
                isSelected={isSelected}
                canSelectMoreChoices={canSelectMoreChoices}
                isMasked={!!response?.maskedOptions?.includes(choice.id)}
                handleOptionClick={handleOptionClick}
                mark={mark}
                allowMultipleKeys={props?.data?.config?.allowMultipleKeys}
                language={player.language}
              />
            );
          })}
        </VSpace>
      </VSpace>
    </div>
  );
};

const StyledOptionOuter = styled(HSpace)`
  display: flex;
  align-items: center;
`;

const StyledOption = styled.div`
  border-radius: 3px;
  display: flex;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  cursor: pointer;
  position: relative;
  background-color: ${colorFromTheme("surface1")};

  ${inputGlow}

  flex: 1;
  padding: 1rem;
  gap: 0.6em;

  &:hover {
    background-color: ${colorFromTheme("surface4")};
  }

  & .markup {
    position: absolute;
    right: 1rem;
    top: 0.6rem;
    width: 20px;
    height: 20px;
  }
`;
const StyledOptionLetter = styled.div`
  width: 3ch;
  font-weight: 700;
  text-transform: capitalize;

  line-height: 1.5em;
`;

const StyledOptionInput = styled.div`
  position: relative;

  height: 1.5em;
  align-content: center;

  & input {
    margin: 0;
    height: 1em;
    width: 1em;
  }
`;

const StyledOptionBody = styled.div`
  flex: 1;

  & p {
    margin: 0.5rem 0 0 0;
  }
  & p:first-child {
    margin: 0;
  }
`;

const StyledOptionMasker = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff88;
  mix-blend-mode: lighten;
`;

// I need a div exactly the same size as the input box
// to make the response masking work
const StyledInputBox = styled.div`
  width: 1em;
  height: 1em;

  position: relative;
`;

const MultipleChoiceOption = ({
  handleOptionClick,
  mark,
  isMasked,
  isSelected,
  canSelectMoreChoices,
  idx,
  choice,
  allowMultipleKeys,
  language,
}: {
  choice: Stem.Option;
  idx: number;
  mark?: "correct" | "incorrect" | undefined;
  language?: string | null;
  isMasked?: boolean;
  isSelected?: boolean;
  canSelectMoreChoices: boolean;
  allowMultipleKeys?: boolean;
  handleOptionClick: (c: Stem.Option) => void;
}) => {
  const examConfig = useContext(ExamConfigContext);
  const examFlags = useContext(ExamFlagsContext);

  const [html, setHtml] = useState<JSX.Element>();

  const getSpeechText = (): string | undefined => {
    const choiceElement = document.querySelector(
      `[data-choice-id="${choice.id}"] .item-player-mc-option-body`,
    );

    if (choiceElement) {
      return extractSpeechText(choiceElement as HTMLElement) || undefined;
    }

    return extractSpeechText(choice.body || "") || undefined;
  };

  const speechText = getSpeechText();

  // Use speechText if available, otherwise use rendered html for TTS
  const { ttsChildren, state, play, pause } = useTts({
    children: speechText || html,
    markTextAsSpoken: true,
    lang: language || "en",
  });

  const showReadAloud = examConfig?.enableReadAloud && examFlags?.textToSpeech;

  return (
    <StyledOptionOuter>
      {showReadAloud && (
        <Button
          title={state.isPlaying ? "Click to Pause" : "Click to Speak"}
          onClick={() => (state.isPlaying ? pause() : play())}
          variant="outlined"
          icon={state.isPlaying ? <IconPauseLine /> : <IconPlayLine />}
        />
      )}
      <StyledOption
        key={choice.id}
        data-cy="item-player-mc-option"
        data-choice-id={choice.id}
        onClick={() => {
          handleOptionClick(choice);
        }}
      >
        {mark && <MarkComponent type={mark} />}
        {isMasked && <StyledOptionMasker />}
        <StyledOptionInput>
          <StyledInputBox>
            <input
              type={allowMultipleKeys ? "checkbox" : "radio"}
              checked={!!isSelected}
              disabled={!isSelected && !canSelectMoreChoices}
              onChange={() => {
                // Prevent default behavior of radio/checkbox
                // as we handle the click manually
              }}
            />
            {isMasked && (
              <svg
                viewBox="10 10 20 20"
                style={{
                  position: "absolute",
                  // firefox color match
                  // stroke: "#8f8f8f",
                  // chrome color match
                  stroke: "#767676",
                  left: "0px",
                  top: "0px",
                }}
                width="100%"
                height="100%"
              >
                <g stroke-linecap="round" stroke-width="2">
                  <line x1="11.5" y1="12.5" x2="28.5" y2="27.5"></line>
                  <line x1="28.5" y1="12.5" x2="11.5" y2="27.5"></line>
                </g>
              </svg>
            )}
          </StyledInputBox>
        </StyledOptionInput>
        <StyledOptionLetter>{letters(idx)}.</StyledOptionLetter>
        <StyledOptionBody className={state.isPlaying ? "speaking" : ""}>
          <RichContentDisplay
            className="item-player-mc-option-body"
            wrapper={(html: JSX.Element) => {
              setHtml(html);
              // Use ttsChildren when TTS is playing for highlighting
              if (state.isPlaying && !speechText) {
                return <>{ttsChildren}</>;
              }
              return html;
            }}
            content={choice.body}
          />
        </StyledOptionBody>
      </StyledOption>
    </StyledOptionOuter>
  );
};

export default MultipleChoice;
